import { useEffect, useRef, useState } from 'react';

/**
 * How far outside the viewport counts as near. This is the number that decides how many sections ask
 * for their rows at once: it applies on both sides, so what counts as near is the viewport plus twice
 * this — and every section inside it on first paint fires its own request, together.
 *
 * Two screens of head start bought prefetch at the price of a thundering herd. Roughly one screen
 * still gets a section asked for before a fast scroll arrives at it, without the whole page asking at
 * the same moment.
 */
const NEAR_MARGIN_PX = 800;

/**
 * Ref for an element, and whether it has come near the viewport yet.
 *
 * Sticky on purpose: once a section has been asked for it stays asked for, so scrolling back and
 * forth past it does not start the request again.
 */
export function useNearViewport<T extends Element = HTMLDivElement>(
  rootMargin: string = `${NEAR_MARGIN_PX}px`,
): [React.MutableRefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [isNear, setNear] = useState(false);

  useEffect(() => {
    if (isNear) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    // The IntersectionObserver default root is the viewport, so this works whichever ancestor
    // actually scrolls — the app's own layout scrolls an inner element, not the body.
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) setNear(true);
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isNear, rootMargin]);

  return [ref, isNear];
}
